import { format } from 'date-fns';
import type {
  FieldDefinition,
  FieldGroup,
  FieldGroupWithFields,
  FieldColumnDefinition,
  FieldFilterOption,
  FieldType,
  MultiValueEntry,
  AddressValue,
  ListItem,
} from '@/types/fieldRegistry';
import { formatDocument, type DocumentFieldType } from '@/lib/document-mask';
import { normalizeEstado } from '@/lib/estado-br';

// ============================================================================
// Leitura e escrita de valores
// ============================================================================

/**
 * Lê o valor de um campo em uma entidade.
 * Para campos sistema, lê de entity[system_column].
 * Para campos custom, lê de entity.custom_data[field_name].
 */
function getFieldValue(entity: Record<string, any>, field: FieldDefinition): any {
  if (field.is_system && field.system_column) {
    return entity[field.system_column] ?? null;
  }
  return entity?.custom_data?.[field.field_name] ?? null;
}

/**
 * Define o valor de um campo em uma entidade.
 * Retorna um objeto parcial com as mudanças a aplicar.
 * Para campos sistema: { [system_column]: value }
 * Para campos custom: { custom_data: { ...existing, [field_name]: value } }
 */
function getFieldUpdate(
  entity: Record<string, any>,
  field: FieldDefinition,
  value: any
): Record<string, any> {
  if (field.is_system && field.system_column) {
    return { [field.system_column]: value };
  }
  return {
    custom_data: {
      ...(entity?.custom_data || {}),
      [field.field_name]: value,
    },
  };
}

/**
 * Retorna o identificador usado para referenciar o campo em queries/filtros.
 * Campos sistema: system_column (ex: "name", "amount")
 * Campos custom: "custom_data.field_name" (ex: "custom_data.placa_veiculo")
 */
function getFieldKey(field: FieldDefinition): string {
  if (field.is_system && field.system_column) {
    return field.system_column;
  }
  return `custom_data.${field.field_name}`;
}

// ============================================================================
// Agrupamento
// ============================================================================

/**
 * Id do grupo virtual "Outros" — bucket de exibição para campos com
 * `group_id = NULL`. NÃO é um id real de `field_groups`: nunca pode ser
 * gravado na coluna `group_id` (uuid). Selecionar este valor na UI significa
 * "sem grupo" e deve ser convertido para `null` antes de ir ao banco.
 */
export const UNGROUPED_GROUP_ID = '__ungrouped__';
export const UNGROUPED_GROUP_NAME = 'Outros';

/**
 * Agrupa campos por grupo e ordena por display_order.
 * Campos sem grupo vão para um grupo virtual "Outros".
 */
export function groupFieldsByGroup(
  fields: FieldDefinition[],
  groups: FieldGroup[]
): FieldGroupWithFields[] {
  const groupMap = new Map<string, FieldGroupWithFields>();

  // Inicializar grupos ordenados
  const sortedGroups = [...groups].sort((a, b) => a.display_order - b.display_order);
  for (const group of sortedGroups) {
    groupMap.set(group.id, { group, fields: [] });
  }

  // Grupo virtual para campos sem grupo
  const ungroupedId = UNGROUPED_GROUP_ID;
  const ungroupedGroup: FieldGroupWithFields = {
    group: {
      id: ungroupedId,
      org_id: '',
      entity_type: groups[0]?.entity_type ?? fields[0]?.page_type ?? 'leads',
      name: UNGROUPED_GROUP_NAME,
      display_order: 999,
      is_collapsible: true,
      is_default_collapsed: false,
      is_system: false,
      created_at: '',
      updated_at: '',
    },
    fields: [],
  };

  // Distribuir campos nos grupos
  const sortedFields = [...fields].sort((a, b) => a.display_order - b.display_order);
  for (const field of sortedFields) {
    if (field.group_id && groupMap.has(field.group_id)) {
      groupMap.get(field.group_id)!.fields.push(field);
    } else {
      ungroupedGroup.fields.push(field);
    }
  }

  const result = Array.from(groupMap.values());
  if (ungroupedGroup.fields.length > 0) {
    result.push(ungroupedGroup);
  }

  return result;
}

// ============================================================================
// Conversão para colunas de tabela
// ============================================================================

/**
 * Converte campos com is_visible_in_list em definições de coluna
 * compatíveis com useColumnVisibility.
 */
function fieldsToColumns(fields: FieldDefinition[]): FieldColumnDefinition[] {
  return fields
    .filter((f) => f.is_visible_in_list || f.is_system)
    .sort((a, b) => a.display_order - b.display_order)
    .map((field) => ({
      id: field.is_system && field.system_column ? field.system_column : `cf_${field.field_name}`,
      label: field.field_label,
      defaultVisible: field.is_visible_in_list,
      fieldType: field.field_type,
      isSystem: field.is_system,
      systemColumn: field.system_column,
      fieldName: field.field_name,
      sortable: field.is_sortable,
      width: getDefaultWidth(field.field_type),
    }));
}

function getDefaultWidth(fieldType: FieldType): number {
  const widths: Record<FieldType, number> = {
    text: 180,
    number: 120,
    date: 140,
    select: 150,
    textarea: 220,
    phone: 160,
    email_field: 200,
    monetary: 140,
    multi_select: 180,
    user: 160,
    address: 220,
    autocomplete: 180,
    time: 100,
    time_range: 160,
    entity_reference: 180,
    labels: 180,
    list: 280,
    attachment: 220,
    cpf: 150,
    cep: 120,
    rg: 140,
    estado: 170,
  };
  return widths[fieldType] ?? 150;
}

// ============================================================================
// Conversão para opções de filtro
// ============================================================================

/**
 * Converte campos filtráveis em opções para o FilterModal.
 */
function fieldsToFilterOptions(fields: FieldDefinition[]): FieldFilterOption[] {
  return fields
    .filter((f) => f.is_filterable)
    .sort((a, b) => a.display_order - b.display_order)
    .map((field) => ({
      value: getFieldKey(field),
      label: field.field_label,
      type: field.field_type,
      options: field.field_options ?? undefined,
      isSystem: field.is_system,
    }));
}

// ============================================================================
// Formatação de exibição
// ============================================================================

/**
 * Formata o valor de um campo para exibição em texto.
 */
function getFieldDisplayValue(entity: Record<string, any>, field: FieldDefinition): string {
  const value = getFieldValue(entity, field);
  if (value === null || value === undefined || value === '') return '';

  switch (field.field_type) {
    case 'date': {
      try {
        return format(new Date(value), 'dd/MM/yyyy');
      } catch {
        return String(value);
      }
    }

    case 'monetary': {
      const num = typeof value === 'number' ? value : parseFloat(value);
      if (isNaN(num)) return String(value);
      return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    }

    case 'phone':
    case 'email_field': {
      if (Array.isArray(value)) {
        return (value as MultiValueEntry[])
          .map((entry) => `${entry.value}${entry.label ? ` (${entry.label})` : ''}`)
          .join(', ');
      }
      return String(value);
    }

    case 'multi_select': {
      if (Array.isArray(value)) return value.join(', ');
      return String(value);
    }

    case 'address': {
      const addr = value as AddressValue;
      return [addr.street, addr.city, addr.state, addr.zip, addr.country]
        .filter(Boolean)
        .join(', ');
    }

    case 'time_range': {
      if (value && typeof value === 'object' && 'start' in value && 'end' in value) {
        return `${value.start} - ${value.end}`;
      }
      return String(value);
    }

    case 'labels': {
      if (Array.isArray(value)) {
        return value.map((l: any) => (typeof l === 'string' ? l : l.text)).join(', ');
      }
      return String(value);
    }

    case 'user':
    case 'entity_reference':
      // Esses tipos retornam IDs - a exibição depende de lookup externo
      return String(value);

    case 'list': {
      if (Array.isArray(value)) {
        return (value as ListItem[])
          .map((item) => `${item.key ? `${item.key}: ` : ''}${item.value}`)
          .join('; ');
      }
      return String(value);
    }

    // Documento: o valor é guardado só com dígitos, então sem este ramo a
    // coluna de lista mostraria 97652989434 em vez de 976.529.894-34.
    case 'cpf':
    case 'cep':
    case 'rg':
      return formatDocument(field.field_type as DocumentFieldType, value);

    // Mostra o nome oficial mesmo que o banco tenha 'SÃO PAULO' ou 'SP'.
    case 'estado':
      return normalizeEstado(value);

    default:
      return String(value);
  }
}

// ============================================================================
// Filtragem de campos por visibilidade
// ============================================================================

function getFieldsForAddForm(fields: FieldDefinition[]): FieldDefinition[] {
  return fields.filter((f) => f.is_visible_in_add_form);
}

function getFieldsForDetail(fields: FieldDefinition[]): FieldDefinition[] {
  return fields.filter((f) => f.is_visible_in_detail);
}

function getFieldsForList(fields: FieldDefinition[]): FieldDefinition[] {
  return fields.filter((f) => f.is_visible_in_list);
}

function getCustomFields(fields: FieldDefinition[]): FieldDefinition[] {
  return fields.filter((f) => !f.is_system);
}

function getSystemFields(fields: FieldDefinition[]): FieldDefinition[] {
  return fields.filter((f) => f.is_system);
}
